import SettingsPageLayout, { SettingsPageLoading, settingsPageTitleClass } from "../../../components/shared/SettingsPageLayout";
import React from "react";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Dialog from "@mui/material/Dialog";
import { toast } from "react-toastify";

import {
  API_create_billing_portal_session,
  API_get_billed_locations,
  API_get_billing_settings,
  API_request_location_change,
  API_update_billing_settings,
} from "../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import RightworkSelect from "../../../components/shared/RightworkSelect";

const buttonFocusClass = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2";
const primaryButtonClass = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50 ${buttonFocusClass}`;
const secondaryButtonClass = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${buttonFocusClass}`;


const LOCATION_STATUS_STYLES = {
  billable: "border-emerald-200 bg-emerald-50 text-emerald-700",
  setting_up: "border-amber-200 bg-amber-50 text-amber-700",
  inactive: "border-slate-200 bg-slate-100 text-slate-600",
};


function getComparableIds(ids = []) {
  return [...ids].map(String).sort();
}


function getLocationStatusDisplay(location) {
  if (location.onboarding_status === "inactive" || location.status === "closed") {
    return {
      badgeText: "Inactive",
      badgeClassName: LOCATION_STATUS_STYLES.inactive,
    };
  }

  if (location.is_billable) {
    return {
      badgeText: "Billable",
      badgeClassName: LOCATION_STATUS_STYLES.billable,
    };
  }

  return {
    badgeText: "Setting up",
    badgeClassName: LOCATION_STATUS_STYLES.setting_up,
  };
}


function buildFormStateFromSettings(settingsPayload) {
  return {
    superUserAdminIds: settingsPayload?.super_user_admin_user_ids || [],
    billingAdminIds: settingsPayload?.billing_admin_user_ids || [],
    accountingPrimaryEmail: settingsPayload?.accounting_primary_email || "",
  };
}


function getEmptyFormState() {
  return buildFormStateFromSettings(null);
}


function areArraysEqual(left = [], right = []) {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => value === right[index]);
}


function hasBillingFormChanges(formState, billingSettings) {
  const initialFormState = buildFormStateFromSettings(billingSettings);

  return !(
    areArraysEqual(
      getComparableIds(formState.superUserAdminIds),
      getComparableIds(initialFormState.superUserAdminIds)
    ) &&
    areArraysEqual(
      getComparableIds(formState.billingAdminIds),
      getComparableIds(initialFormState.billingAdminIds)
    ) &&
    formState.accountingPrimaryEmail.trim() === initialFormState.accountingPrimaryEmail.trim()
  );
}


function getTodayIsoDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


function formatRequestDate(dateValue) {
  if (!dateValue) {
    return null;
  }

  const parsedDate = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


function getInitialRequestModalState() {
  return {
    isOpen: false,
    location: null,
    requestedAction: "add",
    effectiveDate: getTodayIsoDate(),
    message: "",
    submitting: false,
  };
}


function UserChip({ user, onRemove }) {
  return (
    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm text-violet-900">
      <div className="min-w-0">
        <p className="truncate font-medium">{user.name}</p>
        {user.label && (
          <p className="text-[10px] uppercase tracking-[0.16em] text-violet-600">
            {user.label}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(user.id)}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-white hover:text-slate-700 ${buttonFocusClass}`}
        aria-label={`Remove ${user.name}`}
      >
        <CloseRoundedIcon style={{ fontSize: "16px" }} />
      </button>
    </div>
  );
}


function UserSearchPicker({
  users,
  selectedIds,
  onToggle,
  searchPlaceholder,
}) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const selectedUsers = React.useMemo(
    () => users.filter((user) => selectedIds.includes(user.id)),
    [users, selectedIds]
  );
  const filteredUsers = React.useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }
    return users
      .filter((user) => !selectedIds.includes(user.id))
      .filter((user) => {
        const name = (user.name || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        return name.includes(normalizedQuery) || email.includes(normalizedQuery);
      })
      .slice(0, 8);
  }, [normalizedQuery, selectedIds, users]);

  return (
    <div>
      <div>
        <label className="block">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 transition-colors focus-within:border-violet-300">
            <SearchRoundedIcon fontSize="small" className="text-slate-400" />
            <input
              type="text"
              aria-label={searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full border-0 bg-transparent p-0 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              placeholder={searchPlaceholder}
            />
          </div>
        </label>

        {normalizedQuery && (
          <div className="mt-2 rounded-xl border border-slate-200 bg-white">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => {
                    onToggle(user.id);
                    setSearchQuery("");
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-violet-50 ${buttonFocusClass} ${
                    index !== filteredUsers.length - 1 ? "border-b border-slate-100" : ""
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{user.name}</p>
                    <p className="truncate text-xs text-slate-500">{user.email}</p>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-violet-700">
                    <CheckRoundedIcon style={{ fontSize: "14px" }} />
                    Add
                  </span>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-slate-500">
                No matching users found.
              </div>
            )}
          </div>
        )}
      </div>

      {selectedUsers.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedUsers.map((user) => (
            <UserChip
              key={user.id}
              user={user}
              onRemove={onToggle}
            />
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-slate-500">No additional users selected.</p>
      )}
    </div>
  );
}


function BillingRequestModal({ modalState, onClose, onChange, onSubmit }) {
  if (!modalState.isOpen || !modalState.location) {
    return null;
  }

  const actionLabel = modalState.requestedAction === "remove" ? "remove" : "add";
  const actionTitle = modalState.requestedAction === "remove" ? "Request billing removal" : "Request billing addition";

  return (
    <Dialog open onClose={modalState.submitting ? undefined : onClose} fullWidth maxWidth="sm"
      aria-labelledby="billing-request-title" aria-describedby="billing-request-description"
      PaperProps={{ sx: { borderRadius: "16px", m: 2, width: "calc(100% - 32px)" } }}>
      <div className="p-6 sm:p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-600">
              Billing request
            </p>
            <h2 id="billing-request-title" className="mt-2 text-xl font-semibold text-slate-900">
              {actionTitle} for {modalState.location.store_name}
            </h2>
            <p id="billing-request-description" className="mt-2 text-sm text-slate-600">
              Rightwork will review your request to {actionLabel} billing. Submitting a request does not immediately change your bill or location access.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={modalState.submitting}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60 ${buttonFocusClass}`}
            aria-label="Close billing request modal"
          >
            <CloseRoundedIcon />
          </button>
        </div>

        <fieldset disabled={modalState.submitting} className="mt-6 min-w-0 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Requested effective date
            </span>
            <input
              type="date"
              value={modalState.effectiveDate}
              onChange={(event) => onChange("effectiveDate", event.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition-colors focus:border-violet-300"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Message (required)
            </span>
            <textarea
              value={modalState.message}
              onChange={(event) => onChange("message", event.target.value)}
              className="mt-2 min-h-[136px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition-colors focus:border-violet-300"
              placeholder="Add context for the billing change request."
            />
          </label>
        </fieldset>

        <div className="mt-6 flex items-center justify-end gap-3 sm:flex-col-reverse sm:items-stretch">
          <button
            type="button"
            onClick={onClose}
            disabled={modalState.submitting}
            className={secondaryButtonClass}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={modalState.submitting}
            className={primaryButtonClass}
          >
            <SaveRoundedIcon fontSize="small" />
            {modalState.submitting ? "Submitting..." : "Submit request"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}


function BilledLocationRow({ location, onRequestChange, disabled }) {
  const requestedChange = location.requested_change;
  const locationStatus = getLocationStatusDisplay(location);
  const action = location.is_billable ? "remove" : "add";

  return (
    <li className="px-5 py-4 sm:px-4">
      <div className="flex items-center justify-between gap-4 md:flex-col md:items-stretch">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
            <StoreRoundedIcon fontSize="small" />
          </span>
          <div className="min-w-0">
            <p className="break-words text-sm font-semibold text-slate-900">{location.store_name}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${locationStatus.badgeClassName}`}>
                {locationStatus.badgeText}
              </span>
              {requestedChange && (
                <span className="text-xs font-medium text-violet-700">
                  {requestedChange.requested_action === "remove" ? "Removal requested" : "Addition requested"}
                </span>
              )}
            </div>
          </div>
        </div>
        <button type="button" disabled={disabled} onClick={() => onRequestChange(location, action)}
          aria-label={`${requestedChange ? "Send another billing request" : action === "remove" ? "Request billing removal" : "Request billing addition"} for ${location.store_name}`}
          className={`${secondaryButtonClass} shrink-0 text-xs`}>
          {requestedChange ? "Send another request" : action === "remove" ? "Request removal" : "Request addition"}
        </button>
      </div>
      {requestedChange && (
        <div className="mt-3 rounded-lg bg-violet-50 px-3 py-2.5 text-xs leading-5 text-slate-600">
          <p className="font-medium text-violet-900">
            Awaiting review · Requested for {formatRequestDate(requestedChange.effective_date) || "a date to be confirmed"}
          </p>
          {requestedChange.message && <p className="mt-1 break-words">{requestedChange.message}</p>}
          {requestedChange.requested_by_email && <p className="mt-1 break-all text-slate-500">Requested by {requestedChange.requested_by_email}</p>}
        </div>
      )}
    </li>
  );
}


function BillingOverview({ settings, locations, portalLoading, hasUnsavedChanges, onOpenPortal, onEditContact }) {
  const isLinked = Boolean(settings.stripe_connected);
  const hasEmail = Boolean(settings.accounting_primary_email?.trim());
  const supportUrl = `mailto:will@getrightwork.com?subject=${encodeURIComponent(`Billing setup for ${settings.company_name || "my company"}`)}`;
  const stats = [
    { label: "Company locations", value: locations.length },
    { label: "Billable locations", value: locations.filter((location) => location.is_billable).length },
    { label: "Pending requests", value: locations.filter((location) => location.requested_change).length },
  ];

  return (
    <section aria-labelledby="billing-overview-title" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-6 p-6 md:flex-col sm:p-4">
        <div className="flex min-w-0 items-start gap-4 sm:gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
            <CreditCardRoundedIcon />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">Payments & invoices</p>
            <h2 id="billing-overview-title" className="mt-1 text-xl font-semibold text-slate-900">
              {isLinked ? "Manage your company billing" : "Let’s get your billing set up"}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
              {isLinked
                ? "View invoices, check your subscription, and manage payment methods in your secure billing portal."
                : "Add your billing contact, then connect with Rightwork to arrange your plan and payment setup."}
            </p>
            {isLinked && hasUnsavedChanges && <p className="mt-2 text-xs font-medium text-violet-700">Save or discard your changes before opening the billing portal.</p>}
          </div>
        </div>
        {isLinked ? (
          <button type="button" onClick={onOpenPortal} disabled={portalLoading || hasUnsavedChanges}
            className={`${primaryButtonClass} shrink-0 md:w-full`}>
            {portalLoading ? "Opening portal..." : "Manage payments & invoices"}
            <OpenInNewRoundedIcon style={{ fontSize: 18 }} />
          </button>
        ) : (
          <a href={supportUrl} className={`${primaryButtonClass} shrink-0 md:w-full`}>
            Contact Rightwork <ArrowForwardRoundedIcon style={{ fontSize: 18 }} />
          </a>
        )}
      </div>
      {!isLinked && (
        <ol className="mx-6 mb-6 grid grid-cols-2 gap-3 md:grid-cols-1 sm:mx-4 sm:mb-4">
          <li className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${hasEmail ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>
              {hasEmail ? <CheckRoundedIcon style={{ fontSize: 16 }} /> : "1"}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">{hasEmail ? "Billing contact saved" : "Add a billing contact"}</p>
              <p className="mt-1 break-words text-xs leading-5 text-slate-500">{hasEmail ? settings.accounting_primary_email : "Choose where billing messages should go."}</p>
              {!hasEmail && settings.can_edit && <button type="button" onClick={onEditContact} className={`mt-2 rounded text-xs font-semibold text-violet-700 ${buttonFocusClass}`}>Add billing email</button>}
            </div>
          </li>
          <li className="flex items-start gap-3 rounded-xl bg-slate-50 p-4">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-semibold text-violet-700">2</span>
            <div>
              <p className="text-sm font-medium text-slate-900">Arrange your billing setup</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Rightwork will help confirm pricing and connect your billing account.</p>
            </div>
          </li>
        </ol>
      )}
      <dl className="grid grid-cols-3 border-t border-slate-200 bg-slate-50/60 sm:grid-cols-1">
        {stats.map((stat, index) => (
          <div key={stat.label} className={`px-6 py-4 sm:flex sm:items-center sm:justify-between sm:px-4 ${index ? "border-l border-slate-200 sm:border-l-0 sm:border-t" : ""}`}>
            <dt className="text-xs font-medium text-slate-500">{stat.label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 sm:mt-0">{stat.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}


export default function BillingSettingsDisplayConfigurations(props) {
  const companyOptions = React.useMemo(() => {
    const unique = [];
    props.storeNames?.forEach((store) => {
      if (!unique.some((company) => company.id === store.company)) {
        unique.push({
          id: store.company,
          name: store.company_info?.company_name || store.company_info?.name,
        });
      }
    });
    return unique;
  }, [props.storeNames]);

  const [companyId, setSelectedCompanyId] = React.useState(() => {
    if (props.settingsCompanyId) return props.settingsCompanyId;
    if (props.homeCompany) {
      return props.homeCompany;
    }
    return companyOptions[0]?.id || null;
  });
  const selectedCompanyId = props.settingsCompanyId || companyId;
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const [billingSettings, setBillingSettings] = React.useState(null);
  const [billedLocations, setBilledLocations] = React.useState([]);
  const [requestModal, setRequestModal] = React.useState(getInitialRequestModalState);
  const [formState, setFormState] = React.useState(getEmptyFormState);
  const [locationSearch, setLocationSearch] = React.useState("");
  const [locationFilter, setLocationFilter] = React.useState("all");
  const billingEmailRef = React.useRef(null);
  const loadRequestRef = React.useRef(0);
  const formDirtyRef = React.useRef(false);
  const selectedCompanyIdRef = React.useRef(selectedCompanyId);
  selectedCompanyIdRef.current = selectedCompanyId;

  React.useEffect(() => {
    if (!selectedCompanyId && companyOptions[0]?.id) {
      setSelectedCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, selectedCompanyId]);

  React.useEffect(() => {
    if (!props.homeCompany) {
      return;
    }
    setSelectedCompanyId((current) => current || props.homeCompany);
  }, [props.homeCompany]);

  React.useEffect(() => {
    setBillingSettings(null);
    setBilledLocations([]);
    setFormState(getEmptyFormState());
    setRequestModal(getInitialRequestModalState());
    setLocationSearch("");
    setLocationFilter("all");
    setLoadError(null);
  }, [selectedCompanyId]);

  const refreshBillingData = React.useCallback(async ({ resetForm = true } = {}) => {
    const requestId = ++loadRequestRef.current;
    if (!selectedCompanyId) {
      setLoading(false);
      return;
    }
    const requestCompanyId = selectedCompanyId;
    setLoading(true);
    setLoadError(null);
    try {
      const [settingsResponse, billedLocationsResponse] = await Promise.all([
        getRightworkAPI(API_get_billing_settings, { company_id: requestCompanyId }),
        getRightworkAPI(API_get_billed_locations, { company_id: requestCompanyId }),
      ]);
      if (selectedCompanyIdRef.current !== requestCompanyId || requestId !== loadRequestRef.current) {
        return;
      }
      const settingsPayload = settingsResponse?.data?.redux?.payload || null;
      const locationsPayload = billedLocationsResponse?.data?.redux?.payload?.locations;
      if (!settingsPayload || !Array.isArray(locationsPayload)) {
        throw new Error("Billing information is unavailable. Please try again.");
      }

      setBillingSettings(settingsPayload);
      setBilledLocations(locationsPayload);
      if (resetForm) setFormState(buildFormStateFromSettings(settingsPayload));
    } catch (error) {
      if (selectedCompanyIdRef.current !== requestCompanyId || requestId !== loadRequestRef.current) {
        return;
      }
      setLoadError(error?.response?.data?.system?.message || "We couldn’t load billing information. Please try again.");
    } finally {
      if (selectedCompanyIdRef.current === requestCompanyId && requestId === loadRequestRef.current) {
        setLoading(false);
      }
    }
  }, [selectedCompanyId]);

  React.useEffect(() => {
    refreshBillingData();
  }, [refreshBillingData]);

  // Refetch when the Operations Assistant changes the billing contact.
  // Seeded at mount so a stale signal does not refire on navigation.
  const opsRefreshTimestamp = props.opsRefreshSignal?.timestamp;
  const lastOpsRefreshRef = React.useRef(opsRefreshTimestamp);
  React.useEffect(() => {
    if (!opsRefreshTimestamp || opsRefreshTimestamp === lastOpsRefreshRef.current) {
      return;
    }
    lastOpsRefreshRef.current = opsRefreshTimestamp;
    if (!props.opsRefreshSignal) return;
    const types = props.opsRefreshSignal.types || [];
    if (types.includes("billing")) {
      refreshBillingData({ resetForm: !formDirtyRef.current });
    }
  }, [opsRefreshTimestamp, props.opsRefreshSignal, refreshBillingData]);

  const toggleUser = (key, userId) => {
    setFormState((current) => {
      const currentIds = current[key];
      const nextIds = currentIds.includes(userId)
        ? currentIds.filter((id) => id !== userId)
        : [...currentIds, userId];
      return {
        ...current,
        [key]: nextIds,
      };
    });
  };

  const handleSave = async () => {
    if (!billingSettings?.can_edit || saving) return;
    if (!billingEmailRef.current?.reportValidity()) return;
    const requestCompanyId = selectedCompanyId;
    setSaving(true);
    try {
      const payload = {
        company_id: requestCompanyId,
        super_user_admin_user_ids: formState.superUserAdminIds,
        billing_admin_user_ids: formState.billingAdminIds,
        accounting_primary_email: formState.accountingPrimaryEmail.trim(),
      };
      const response = await postRightworkAPI(API_update_billing_settings, payload);
      const responsePayload = response?.data?.redux?.payload || null;
      if (!responsePayload) throw new Error("Missing saved billing settings");
      if (selectedCompanyIdRef.current === requestCompanyId) {
        setBillingSettings(responsePayload);
        setFormState(buildFormStateFromSettings(responsePayload));
      }
      toast.success("Billing settings updated.", { autoClose: 2500 });
      if (responsePayload?.stripe_sync_result?.status === "failed") {
        toast.error(
          responsePayload?.stripe_sync_result?.error || "Stripe sync failed.",
          { autoClose: 3500 }
        );
      }
      await props.refreshMemberStores?.();
      if (selectedCompanyIdRef.current === requestCompanyId) await refreshBillingData();
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to update billing settings.",
        { autoClose: 3500 }
      );
    } finally {
      setSaving(false);
    }
  };

  const openStripePortal = async () => {
    if (!billingSettings?.stripe_connected || portalLoading || formDirtyRef.current) {
      return;
    }

    const requestCompanyId = selectedCompanyId;
    setPortalLoading(true);
    try {
      const response = await postRightworkAPI(API_create_billing_portal_session, {
        company_id: requestCompanyId,
        return_url: window.location.href,
      });
      const portalUrl = response?.data?.redux?.payload?.url;
      if (selectedCompanyIdRef.current !== requestCompanyId) return;
      if (!portalUrl) throw new Error("Missing billing portal URL");
      // Same-tab navigation avoids popup blockers after the asynchronous request.
      window.location.assign(portalUrl);
    } catch (error) {
      toast.error(
        error?.response?.data?.system?.message || "Unable to open the Stripe portal.",
        { autoClose: 3500 }
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const openLocationRequestModal = (location, requestedAction) => {
    const requestedChange = location?.requested_change;
    const isSameAction = requestedChange?.requested_action === requestedAction;

    setRequestModal({
      isOpen: true,
      location,
      requestedAction,
      effectiveDate: (isSameAction && requestedChange?.effective_date) || getTodayIsoDate(),
      message: (isSameAction && requestedChange?.message) || "",
      submitting: false,
    });
  };

  const closeLocationRequestModal = () => {
    setRequestModal(getInitialRequestModalState());
  };

  const updateRequestModalField = (fieldName, value) => {
    setRequestModal((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const submitLocationChangeRequest = async () => {
    if (!requestModal.location?.store_id || !billingSettings?.can_edit || requestModal.submitting) {
      return;
    }
    const requestCompanyId = selectedCompanyId;
    if (!requestModal.effectiveDate) {
      toast.error("Please choose an effective date.", { autoClose: 3000 });
      return;
    }
    if (!requestModal.message.trim()) {
      toast.error("Please enter a message for the request.", { autoClose: 3000 });
      return;
    }

    setRequestModal((current) => ({
      ...current,
      submitting: true,
    }));

    try {
      await postRightworkAPI(API_request_location_change, {
        company_id: requestCompanyId,
        store_id: requestModal.location.store_id,
        requested_action: requestModal.requestedAction,
        effective_date: requestModal.effectiveDate,
        message: requestModal.message.trim(),
      });
      toast.success("Billing request submitted.", { autoClose: 2500 });
      if (selectedCompanyIdRef.current === requestCompanyId) await refreshBillingData({ resetForm: false });
      if (selectedCompanyIdRef.current === requestCompanyId) {
        setRequestModal(getInitialRequestModalState());
      }
    } catch (error) {
      if (selectedCompanyIdRef.current !== requestCompanyId) return;
      toast.error(
        error?.response?.data?.system?.message || "Unable to submit the billing request.",
        { autoClose: 3500 }
      );
      setRequestModal((current) => ({
        ...current,
        submitting: false,
      }));
    }
  };

  const hasUnsavedChanges = React.useMemo(
    () => hasBillingFormChanges(formState, billingSettings),
    [billingSettings, formState]
  );
  formDirtyRef.current = hasUnsavedChanges;
  const canEdit = Boolean(billingSettings?.can_edit);
  const controlsDisabled = saving || loading || !canEdit;
  const filters = [
    { value: "all", label: "All locations" },
    { value: "billable", label: "Billable" },
    { value: "setting_up", label: "Setting up" },
    { value: "inactive", label: "Inactive" },
    { value: "pending", label: "Pending requests" },
  ];
  const filteredLocations = billedLocations.filter((location) => {
    const matchesSearch = (location.store_name || "").toLowerCase().includes(locationSearch.trim().toLowerCase());
    const status = getLocationStatusDisplay(location).badgeText;
    const matchesFilter = locationFilter === "all"
      || (locationFilter === "pending" && Boolean(location.requested_change))
      || (locationFilter === "billable" && status === "Billable")
      || (locationFilter === "setting_up" && status === "Setting up")
      || (locationFilter === "inactive" && status === "Inactive");
    return matchesSearch && matchesFilter;
  });

  if (selectedCompanyId && loading && !billingSettings) {
    return <SettingsPageLoading />;
  }

  return (
    <SettingsPageLayout className={hasUnsavedChanges ? "pb-32" : ""}>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4 md:flex-col md:items-stretch">
          <div>
            <p className="mb-1 text-sm font-medium text-slate-500">{billingSettings?.company_name || "Company settings"}</p>
            <h1 className={settingsPageTitleClass}>Company billing</h1>
            <p className="mt-1 text-sm text-slate-500">Manage payments, billing contacts, and locations in one place.</p>
          </div>
          {!props.settingsCompanyId && companyOptions.length > 1 && (
            <div className="w-full max-w-[260px] md:max-w-none">
              <label id="billing-company-label" htmlFor="billing-company" className="mb-1 block text-xs font-medium text-slate-500">Company</label>
              <RightworkSelect id="billing-company" value={selectedCompanyId || ""}
                disabled={saving || portalLoading || hasUnsavedChanges || requestModal.isOpen}
                onChange={(event) => setSelectedCompanyId(event.target.value)}
                options={companyOptions.map((company) => ({ value: company.id, label: company.name }))} />
              {hasUnsavedChanges && <p className="mt-1 text-xs text-slate-500">Save or discard changes to switch companies.</p>}
            </div>
          )}
        </div>

        {!selectedCompanyId && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <h2 className="text-base font-semibold text-slate-900">No company selected</h2>
            <p className="mt-2 text-sm text-slate-500">Billing information will appear when a company is available to your account.</p>
          </div>
        )}
        {loadError && (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 p-4 sm:flex-col sm:items-stretch">
            <div><p className="text-sm font-medium text-rose-900">{loadError}</p>
              {billingSettings && <p className="mt-1 text-xs text-rose-700">The information below may be out of date.</p>}</div>
            <button type="button" onClick={() => refreshBillingData({ resetForm: !hasUnsavedChanges })} disabled={loading} className={secondaryButtonClass}>Try again</button>
          </div>
        )}
        {billingSettings && (
          <>
            <BillingOverview settings={billingSettings} locations={billedLocations}
              hasUnsavedChanges={hasUnsavedChanges}
              portalLoading={portalLoading || loading} onOpenPortal={openStripePortal}
              onEditContact={() => billingEmailRef.current?.focus()} />

            <div className="grid grid-cols-[minmax(0,1fr)_340px] items-start gap-6 lg:grid-cols-1">
              <section aria-labelledby="billing-locations-title" className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="p-5 sm:p-4">
                  <h2 id="billing-locations-title" className="text-base font-semibold text-slate-900">Locations</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">Review location status and request billing changes. Rightwork reviews each request before making changes.</p>
                  <div className="mt-4 flex items-center gap-2 rounded-xl border border-slate-200 px-3 focus-within:border-violet-600 focus-within:ring-1 focus-within:ring-violet-600">
                    <SearchRoundedIcon fontSize="small" className="text-slate-400" />
                    <input type="search" aria-label="Search locations" placeholder="Search locations" value={locationSearch}
                      onChange={(event) => setLocationSearch(event.target.value)}
                      className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400" />
                  </div>
                  <div role="group" aria-label="Filter locations" className="mt-3 flex flex-wrap gap-1.5">
                    {filters.map((filter) => (
                      <button key={filter.value} type="button" aria-pressed={locationFilter === filter.value}
                        onClick={() => setLocationFilter(filter.value)}
                        className={`min-h-[36px] rounded-lg px-3 py-2 text-xs font-medium transition-colors ${buttonFocusClass} ${locationFilter === filter.value ? "bg-violet-100 text-violet-800" : "text-slate-600 hover:bg-slate-100"}`}>
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between border-y border-slate-100 bg-slate-50 px-5 py-2.5 text-xs text-slate-500 sm:px-4">
                  <span role="status">{filteredLocations.length} of {billedLocations.length} locations</span>
                  {loading && <span>Refreshing...</span>}
                </div>
                {filteredLocations.length > 0 ? (
                  <ul className="divide-y divide-slate-100">
                    {filteredLocations.map((location) => (
                      <BilledLocationRow key={location.store_id} location={location} disabled={controlsDisabled}
                        onRequestChange={openLocationRequestModal} />
                    ))}
                  </ul>
                ) : (
                  <div className="px-5 py-10 text-center">
                    <StoreRoundedIcon className="text-slate-400" />
                    <p className="mt-2 text-sm font-medium text-slate-900">{billedLocations.length ? "No matching locations" : "No locations yet"}</p>
                    <p className="mt-1 text-sm text-slate-500">{billedLocations.length ? "Try a different name or status." : "Your company’s locations will appear here once they’re added."}</p>
                    {billedLocations.length > 0 && <button type="button" onClick={() => { setLocationSearch(""); setLocationFilter("all"); }} className={`mt-3 rounded text-sm font-semibold text-violet-700 ${buttonFocusClass}`}>Clear filters</button>}
                  </div>
                )}
              </section>

              <div className="min-w-0 space-y-6">
                <section aria-labelledby="billing-contact-title" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-4">
                  <div className="flex items-center gap-2.5">
                    <EmailRoundedIcon fontSize="small" className="text-slate-500" />
                    <h2 id="billing-contact-title" className="text-base font-semibold text-slate-900">Billing contact</h2>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-500">Send invoices and billing messages to the right person.</p>
                  <fieldset disabled={controlsDisabled} className="mt-4 min-w-0">
                    <label htmlFor="billing-email" className="block text-sm font-medium text-slate-700">Billing email</label>
                    <input id="billing-email" ref={billingEmailRef} type="email" value={formState.accountingPrimaryEmail}
                      onChange={(event) => setFormState((current) => ({ ...current, accountingPrimaryEmail: event.target.value }))}
                      placeholder="accounting@company.com" aria-describedby="billing-email-help"
                      className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600 disabled:bg-slate-50" />
                    <p id="billing-email-help" className="mt-2 text-xs leading-5 text-slate-500">Changes are applied when you save.</p>
                  </fieldset>
                  {billingSettings.stripe_sync_status === "failed" && (
                    <div role="status" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                      Your saved billing contact couldn’t be synced to Stripe. Save again to retry or contact Rightwork for help.
                    </div>
                  )}
                </section>
                <section aria-labelledby="billing-help-title" className="rounded-2xl border border-slate-200 p-5 sm:p-4">
                  <h2 id="billing-help-title" className="text-sm font-semibold text-slate-900">Questions about your bill?</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">For pricing, plan changes, or help getting started, reach out to the Rightwork team.</p>
                  <a className={`mt-3 inline-flex min-h-[36px] items-center gap-2 rounded text-sm font-semibold text-violet-700 ${buttonFocusClass}`}
                    href={`mailto:will@getrightwork.com?subject=${encodeURIComponent(`Billing help for ${billingSettings.company_name || "my company"}`)}`}>
                    Contact Rightwork <ArrowForwardRoundedIcon style={{ fontSize: 16 }} />
                  </a>
                </section>
              </div>
            </div>

            <section aria-labelledby="billing-access-title" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-4">
              <div className="flex items-center gap-2.5">
                <PeopleAltRoundedIcon fontSize="small" className="text-slate-500" />
                <h2 id="billing-access-title" className="text-base font-semibold text-slate-900">Who can manage billing</h2>
              </div>
              <p className="mt-2 text-sm text-slate-500">Choose company members who can access billing settings and the billing portal.</p>
              <fieldset disabled={controlsDisabled} className="mt-5 grid min-w-0 grid-cols-2 gap-6 md:grid-cols-1">
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-slate-900">Super admins</h3>
                  <p className="mb-3 mt-1 text-xs leading-5 text-slate-500">These admins also have billing access.</p>
                  <UserSearchPicker users={billingSettings.candidate_users || []} selectedIds={formState.superUserAdminIds}
                    onToggle={(userId) => toggleUser("superUserAdminIds", userId)} searchPlaceholder="Search super admins by name or email" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-slate-900">Billing admins</h3>
                  <p className="mb-3 mt-1 text-xs leading-5 text-slate-500">Additional members who can manage billing.</p>
                  <UserSearchPicker users={billingSettings.candidate_users || []} selectedIds={formState.billingAdminIds}
                    onToggle={(userId) => toggleUser("billingAdminIds", userId)} searchPlaceholder="Search billing admins by name or email" />
                </div>
              </fieldset>
              {(!canEdit) && <p className="mt-4 text-sm text-slate-500">Contact a company billing admin to make changes.</p>}
            </section>
          </>
        )}
      </div>

      {billingSettings && canEdit && (hasUnsavedChanges || billingSettings.stripe_sync_status === "failed") && (
        <div className="sticky bottom-4 z-40 mt-6 flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg sm:flex-col sm:items-stretch">
          <p role="status" className="text-sm text-slate-600">{hasUnsavedChanges ? "You have unsaved billing changes." : "Retry syncing your billing contact."}</p>
          <div className="flex items-center gap-2 sm:justify-end">
            {hasUnsavedChanges && <button type="button" onClick={() => setFormState(buildFormStateFromSettings(billingSettings))} disabled={saving || loading} className={secondaryButtonClass}>Discard</button>}
            <button type="button" onClick={handleSave} disabled={saving || loading} className={primaryButtonClass}>
              <SaveRoundedIcon fontSize="small" />
              {saving ? "Saving..." : "Save billing settings"}
            </button>
          </div>
        </div>
      )}
      <BillingRequestModal modalState={requestModal} onClose={closeLocationRequestModal}
        onChange={updateRequestModalField} onSubmit={submitLocationChangeRequest} />
    </SettingsPageLayout>
  );
}
